import React, { useEffect, useState, useMemo, useRef } from "react";
import getSubfragm from "../../../../graphics/get-subfragm";
import { throttle2 } from "../../../../utils/throttle";
import { prepareSplitHOC } from "../../../../hoc/prepareSplitHOC";
import { SizeController } from "../../../../utils/sizeController";
import { adjustSignalStart } from "../../../../utils/adjustSignalStart";

import Slider from "../../../ui/SliderComponent/SliderComponent";
import { ChooseCmpxGraphMain } from "./choose-cmpx-graph-main/choose-cmpx-graph-main";
import { ChooseCmpxGraphNav } from "./choose-cmpx-graph-nav/choose-cmpx-graph-nav";
import { ReactComponent as SvgMagnify } from "../../../../assets/magnify.svg";
import { ReactComponent as SvgCollapseV } from "../../../../assets/collapse-vertical.svg";
import { ReactComponent as SvgCollapseH } from "../../../../assets/collapse-horizontal.svg";
import { ReactComponent as SvgExpandV } from "../../../../assets/expand-vertical.svg";
import { ReactComponent as SvgExpandH } from "../../../../assets/expand-horizontal.svg";

import { ErrorIndicator, Spinner } from "../../../state-indicators";

import "./choose-cmpx-graph.scss";

const calcSignalRhythmStart = (_Start, _WindowLength, _RhStart, _RhWindowLength) => {
    const step = (_RhWindowLength - _WindowLength) / 2;
    const edge = Math.round(_Start - (_Start % (_RhWindowLength - _WindowLength)));
    _RhStart = _RhStart ?? 0;
    let ans = _RhStart;
    if (_RhStart < edge) ans = Math.round(_Start - (_Start % step) - step); // если целую перемотку делать то убрать - step
    else if (_RhStart > edge) ans = Math.round(_Start - (_Start % step));

    return ans;
};

const ChooseCmpxGraph = props => {
    const [state, setState] = useState("loading"); // "idle, error, loading"
    const [signalData, setSignalData] = useState([]);
    const [rhythmData, setRhythmData] = useState([]);
    const [signalStart, setSignalStart] = useState(null);
    const [navGraphWidth, setNavGraphWidth] = useState(null);
    const [mainGraphWidth, setMainGraphWidth] = useState(null);
    const [cursorPosition, setCursorPosition] = useState(null);
    const [signalRhythmStart, setSignalRhythmStart] = useState(null);
    const [refreshFlag, setRefreshFlag] = useState(false);
    const [start_pnt, setStart_pnt] = useState(null);
    const [end_pnt, setEnd_pnt] = useState(null);
    const peaks = useRef([]);
    const mainGraphRef = useRef(null);
    const isLastComplex = useRef(false);
    const isFirstComplex = useRef(false);
    const hasStartedRef = useRef(false);
    const hasPerparedCursorPosition = useRef(false);

    const { signalLength, loc, defaultScale } = props;

    useEffect(() => {
        const sizeController = new SizeController({
            observable: ".resizer-container",
            changeable: ".popup-window",
            totalSectorsWidth: 38, //columnSpace - 38
            totalSectorsHeight: 10,
            chartCount: 2,
            truncWidthSquares: 1,
            truncHeightSquares: 1,
            mainGraphHeightPercentage: 0.85,
            rhythmGraphHeightPercentage: 0.15
        });
        sizeController.observe();
        const mount = async () => {
            prepareSignalService();
            prepareSignalBounds();
        };

        mount();

        return () => {
            sizeController.stop();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!hasStartedRef.current && mainGraphWidth > 0) {
            setStart();
            hasStartedRef.current = true;
        }
        if (signalStart !== null) {
            if (!hasPerparedCursorPosition.current) {
                prepareCursorPosition();
                hasPerparedCursorPosition.current = true;
            }
            updateGraph();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signalStart, mainGraphWidth]);

    useEffect(() => {
        if (cursorPosition) {
            updatePeaks();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cursorPosition]);

    const prepareSignalService = () => {
        const { signalService } = props;
        signalService.dataPoster.onAnswer = onSignalServiceAnswer;
        signalService.dataPoster.onErroe = onSignalServiceError;
    };

    const prepareSignalBounds = () => {
        if (!props.prepared) {
            return;
        }

        const _signalRhythmStart = calcSignalRhythmStart(signalStart, mainGraphWidth, signalRhythmStart, navGraphWidth);

        setSignalRhythmStart(_signalRhythmStart);
    };

    const updateGraph = () => {
        const { channel } = props.rhythmScale;

        // adjust signalStart so it wouldn't go out of bounds of signalLength
        const adjustedSignalStart = adjustSignalStart(signalStart, mainGraphWidth, signalLength);
        updateSignalData(adjustedSignalStart, Math.min(mainGraphWidth, signalLength), {
            start: signalRhythmStart,
            length: Math.min(navGraphWidth, signalLength - signalRhythmStart),
            channel
        });
        updatePeaks();
    };

    const setStart = async () => {
        const { Q, S } = props;
        const { start } = getSubfragm(signalLength, Q + S / 2, mainGraphWidth);
        setSignalStart(start);
    };

    const prepareCursorPosition = () => {
        const { Q, S } = props;
        const newCursorPosition = Q + S / 2 - signalStart;
        setCursorPosition(newCursorPosition);
    };

    const onSignalServiceAnswer = data => {
        const { lead_signals, rhythm_signal } = data;
        setState("idle");
        setSignalData(lead_signals);
        setRhythmData(rhythm_signal);
    };

    const onSignalServiceError = error => {
        console.log(error);
        setState("error");
    };

    const updateSignalData = (start, length, extraSignal) => {
        props.reqSignalThrottling(start, length, extraSignal);
    };

    const updatePeaks = () => {
        const { complexes, fd, onChanged, Q: currQ } = props;
        let inds = [];
        const { Qs: _qs, Ws: _ws } = complexes;
        for (let i = 0; i < _qs.length; i++) {
            if (_qs[i] < signalStart || _qs[i] > signalStart + mainGraphWidth) {
                continue;
            }
            inds.push(i);
        }
        let Qs = inds.map(i => _qs[i] - signalStart);
        let Ss = inds.map(i => _ws[i] + _qs[i] - signalStart);

        peaks.current = [Qs, Ss];

        let currInd;
        const N = peaks.current[0].length;
        for (let i = 0; i < N; i++) {
            const Qnext = i < N - 1 ? peaks.current[0][i + 1] : undefined,
                S = peaks.current[1][i],
                Q = peaks.current[0][i];
            if (Q < 0 || S > mainGraphWidth) continue;

            if (Qnext === undefined) {
                currInd = i;
                break;
            }

            if (cursorPosition < (S + Qnext) / 2) {
                currInd = i;
                break;
            }
        }

        isFirstComplex.current = complexes.globalFirstCmpxQ && complexes.globalFirstCmpxQ - signalStart === peaks.current[0][currInd];
        isLastComplex.current = complexes.globalLastCmpxQ && complexes.globalLastCmpxQ - signalStart === peaks.current[0][currInd];
        isLastComplex.current = false; // ОТКЛЮЧЕН запрет на выбор послежднегно комплекса
        const notChanged = currQ - signalStart === peaks.current[0][currInd];

        const start_pnt = Math.max(0, peaks.current[0][currInd] - 0.25 * fd); // Точка Q - 0.25 секунды
        const end_pnt = Math.min(mainGraphWidth, peaks.current[1][currInd] + 0.25 * fd); // Точка S + 0.25 секунды

        const Q = peaks.current[0][currInd] + signalStart,
            S = peaks.current[1][currInd] + signalStart;

        onChanged({
            enabled: !(isFirstComplex.current || isLastComplex.current),
            notChanged,
            Q,
            S: S - Q
        });

        setStart_pnt(start_pnt);
        setEnd_pnt(end_pnt);
    };

    const onSliderChange = useMemo(() => {
        return throttle2(value => {
            setSignalStart(value);
            setSignalRhythmStart(Math.floor(value / (navGraphWidth - mainGraphWidth)) * (navGraphWidth - mainGraphWidth));
        }, 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navGraphWidth, mainGraphWidth]);

    const changeScaleX = type => {
        const { changeWholeGraphSettings } = props;
        const [index, graphWidth] = mainGraphRef.current.changeScaleX(type);

        const adjustedSignalStart = adjustSignalStart(signalStart, graphWidth, signalLength);
        setSignalStart(adjustedSignalStart);
        const adjustedSignalRhtyhmStart = Math.floor(signalStart / (navGraphWidth - graphWidth)) * (navGraphWidth - graphWidth);
        setSignalRhythmStart(adjustedSignalRhtyhmStart);

        changeWholeGraphSettings({
            xScaleIndex: index
        });
    };

    const changeScaleY = async type => {
        const { changeWholeGraphSettings } = props;
        const index = await mainGraphRef.current.changeScaleY(type);

        changeWholeGraphSettings({
            yScaleIndex: index
        });
    };
    const sliderMax = Math.max(0, signalLength - mainGraphWidth);

    return (
        <>
            <div className="whole-graph__bar">
                <Slider value={signalStart} max={sliderMax} onChange={onSliderChange} />

                <div className="whole-graph__magnifier">
                    <div className="icon">
                        <SvgMagnify />
                    </div>
                    <div className="divider" />
                    <button
                        type="button"
                        disabled={defaultScale.yScaleIndex === 0}
                        onClick={() => changeScaleY("decrease")}
                        className="button toolbar__button">
                        <SvgCollapseV />
                    </button>
                    <button
                        type="button"
                        disabled={defaultScale.yScaleIndex === props.yScales.length - 1}
                        onClick={() => changeScaleY("increase")}
                        className="button toolbar__button">
                        <SvgExpandV />
                    </button>
                    <button
                        type="button"
                        disabled={defaultScale.xScaleIndex === 0}
                        onClick={() => changeScaleX("decrease")}
                        className="button toolbar__button">
                        <SvgCollapseH />
                    </button>
                    <button
                        type="button"
                        disabled={defaultScale.xScaleIndex === props.xScales.length - 1}
                        onClick={() => changeScaleX("increase")}
                        className="button toolbar__button">
                        <SvgExpandH />
                    </button>
                </div>
            </div>
            <div className="graphContainer">
                <div className={"wholeGraph" + (state === "error" ? " error" : "")}>
                    <div className={"wholeGraph__graphWrap" + (state === "error" ? " error" : "")}>
                        <ChooseCmpxGraphMain
                            ref={mainGraphRef}
                            fd={props.fd}
                            signalData={signalData}
                            signalStart={signalStart}
                            signalLength={signalLength}
                            cursorPosition={cursorPosition}
                            refreshFlag={refreshFlag}
                            isFirstComplex={isFirstComplex.current}
                            Q={props.Q}
                            S={props.S}
                            scale={defaultScale}
                            peaks={peaks.current}
                            start_pnt={start_pnt}
                            end_pnt={end_pnt}
                            userSweepUnits={loc("units.mm_sec")}
                            setMainGraphWidth={setMainGraphWidth}
                            setCursorPosition={setCursorPosition}
                            setRefreshFlag={setRefreshFlag}
                        />
                        {state === "error" ? (
                            <ErrorIndicator withCaption small {...{ errorCaptionKey: "errors.dataUnavailable" }} />
                        ) : state === "loading" ? (
                            <Spinner small transparent />
                        ) : null}
                    </div>
                </div>
                <div className={"wholeGraphRhythm" + (state === "error" ? " error" : "")}>
                    <div className={"wholeGraphRhythm__graphWrap" + (state === "error" ? " error" : "")}>
                        <ChooseCmpxGraphNav
                            signalData={rhythmData}
                            signalStart={signalStart}
                            signalLength={signalLength}
                            mainGraphWidth={mainGraphWidth}
                            refreshFlag={refreshFlag}
                            cursorPosition={cursorPosition}
                            isFirstComplex={isFirstComplex.current}
                            fd={props.fd}
                            Q={props.Q}
                            S={props.S}
                            peaks={peaks.current}
                            start_pnt={start_pnt}
                            end_pnt={end_pnt}
                            signalRhythmStart={signalRhythmStart}
                            userSweepUnits={loc("units.mm_sec")}
                            setNavGraphWidth={setNavGraphWidth}
                            setSignalRhythmStart={setSignalRhythmStart}
                        />
                        {state === "error" ? (
                            <ErrorIndicator withCaption small {...{ errorCaptionKey: "errors.dataUnavailable" }} />
                        ) : state === "loading" ? (
                            <Spinner small transparent />
                        ) : null}
                    </div>
                </div>
            </div>
        </>
    );
};

export default prepareSplitHOC(ChooseCmpxGraph);
